// `/admin/bug-reports/$id`: conteúdo do drawer de um reporte (descrição, anexos, contexto
// técnico e as ações de status). O cabeçalho é do `RouteDrawer` da rota-pai.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { errorMessageOf } from '../api/errors.ts';
import {
  type AdminBugReportDetail,
  adminBugReportDetailQueryOptions,
  adminBugReportKeys,
  type SettableStatus,
  setBugReportStatus,
} from '../api/queries/admin-bug-reports.ts';
import { ALERT_ACK_INVALIDATES } from '../api/queries/admin-overview.ts';
import { ErrorState } from '../components/app/error-state.tsx';
import { CardSkeleton } from '../components/app/skeletons.tsx';
import { Empty } from '../components/kit/empty.tsx';
import { BugReportDetail } from '../features/admin-bug-reports/detail.tsx';
import { isNotFound } from '../features/admin-bug-reports/model.ts';

function LoadedReport({ report }: { report: AdminBugReportDetail }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState(report.admin_note ?? '');

  const mutation = useMutation({
    mutationFn: (status: SettableStatus) => setBugReportStatus(report.id, status, note),
    onSuccess: (_data, status) => {
      // Atualiza o detalhe no cache em vez de refazer o GET: cada GET assina as URLs e grava
      // `bug_report.view_file` na auditoria.
      queryClient.setQueryData<AdminBugReportDetail>(
        adminBugReportKeys.detail(report.id),
        (current) =>
          current === undefined
            ? current
            : {
                ...current,
                status,
                admin_note: note.trim() === '' ? current.admin_note : note.trim(),
                resolved_at: status === 'resolved' ? new Date().toISOString() : null,
              },
      );
      void queryClient.invalidateQueries({ queryKey: [...adminBugReportKeys.all, 'list'] });
      for (const queryKey of ALERT_ACK_INVALIDATES) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return (
    <BugReportDetail
      report={report}
      note={note}
      onNoteChange={setNote}
      pending={mutation.isPending}
      error={mutation.isError ? errorMessageOf(mutation.error) : undefined}
      onSetStatus={(status) => {
        mutation.mutate(status);
      }}
    />
  );
}

function AdminBugReport() {
  const { id } = Route.useParams();
  const detail = useQuery(adminBugReportDetailQueryOptions(id));

  if (detail.isPending) {
    return <CardSkeleton lines={6} />;
  }
  if (isNotFound(detail.error)) {
    return (
      <Empty title="Reporte não encontrado" hint="Este reporte não existe ou o link está errado." />
    );
  }
  if (detail.isError) {
    return (
      <ErrorState
        error={detail.error}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }
  return <LoadedReport key={detail.data.id} report={detail.data} />;
}

export const Route = createFileRoute('/admin/bug-reports/$id')({ component: AdminBugReport });
